import { animate, motion, useMotionValue, useTransform } from 'framer-motion';
import { AnimTap, EColors2, EFonts, EVariables } from 'libs/styles/styling';
import { useAnalytics } from 'libs/utils/site/analytics';
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CtxEventView } from 'session/ctxEventView';
import styled from 'styled-components/macro';

interface Props {
    key: string;
    className?: string;
    number?: number;
    title: string;
    subtitle?: string;

    infoCircle: React.ReactNode;
    linkTo?: string;
    externalLinkTo?: string;
    tileColor: string;
    narrowStyle?: boolean;
}

export const TileButton = (props: Props) => {
    const navigate = useNavigate();
    const analytics = useAnalytics();
    const count = useMotionValue(0);
    const rounded = useTransform(count, (latest) => Math.round(latest));
    const ctxEventView = useContext(CtxEventView);

    useEffect(() => {
        const controls = animate(count, props.number, {
            duration: 1
        });

        return controls.stop;
    }, [props.number]);

    function handleClick() {
        if (props.linkTo) {
            analytics.track('event_ui.navigate.clicked', {
                source: 'tile',
                destination: props.key
            });
            navigate(props.linkTo);
            ctxEventView.setScrollToTop(true);
        } else if (props.externalLinkTo) {
            analytics.track('event_ui.external_link', {
                source: 'tile',
                destination: props.externalLinkTo
            });
            const link = props.externalLinkTo.startsWith('http')
                ? props.externalLinkTo
                : `https://${props.externalLinkTo}`;
            window.open(link, '_blank', 'noreferrer');
        }
    }

    return (
        <S_TileButton
            className={props.className}
            $tileColor={props.tileColor}
            onClick={handleClick}
            {...AnimTap}
        >
            {props.narrowStyle && (
                <S_NarrowStyle>
                    <S_InfoIconContainerNarrow>{props.infoCircle}</S_InfoIconContainerNarrow>
                    {!ctxEventView.eventData.eventInfo.noNumbers && (
                        <S_NumberTextNarrow>{rounded}</S_NumberTextNarrow>
                    )}
                    <S_TitleTextNarrow>{props.title}</S_TitleTextNarrow>
                </S_NarrowStyle>
            )}
            {!props.narrowStyle && (
                <S_RegularStyle>
                    {!ctxEventView.eventData.eventInfo.noNumbers && (
                        <>
                            {props.number != null && (
                                <S_NumberTextRegular>{rounded}</S_NumberTextRegular>
                            )}

                            <S_TitleTextRegular>{props.title}</S_TitleTextRegular>
                        </>
                    )}
                    {ctxEventView.eventData.eventInfo.noNumbers && (
                        <>
                            <S_TitleTextRegular
                                style={{
                                    marginTop: '40px',
                                    fontSize: '17px'
                                }}
                            >
                                {props.title}
                            </S_TitleTextRegular>
                        </>
                    )}

                    {props.subtitle && <S_SubtitleRegular>{props.subtitle}</S_SubtitleRegular>}

                    <S_InfoIconContainerRegular>{props.infoCircle}</S_InfoIconContainerRegular>
                </S_RegularStyle>
            )}
        </S_TileButton>
    );
};

const S_TileButton = styled(motion.div)<{
    $tileColor: string;
    $isNarrow?: boolean;
}>`
    position: relative;
    cursor: pointer;
    width: 100%;
    min-height: 110px;
    display: flex;
    padding: ${(props) => (props.$isNarrow ? '16px 10px 12px 10px' : '16px 20px 16px 24px')};

    display: flex;
    flex-direction: column;
    justify-content: center;
    border-radius: ${EVariables.Radius1};
    background-color: ${(props) => props.$tileColor};
`;

const S_NarrowStyle = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const S_InfoIconContainerNarrow = styled.div`
    width: 40px;
    height: 40px;
    margin-bottom: 10px;
`;

const S_NumberTextNarrow = styled(motion.div)`
    color: ${EColors2.DARK};
    font-family: ${EFonts.Bold};
    font-size: 17px;
    margin-bottom: 7px;
`;

const S_TitleTextNarrow = styled.div`
    color: ${EColors2.DARK};
    font-family: ${EFonts.Medium};
    font-size: 14px;
`;

const S_RegularStyle = styled.div``;

const S_NumberTextRegular = styled(motion.div)`
    color: ${EColors2.DARK};
    font-family: ${EFonts.Bold};
    font-size: 28px;
    margin-bottom: 7px;
`;

const S_TitleTextRegular = styled.div`
    color: ${EColors2.DARK};
    font-family: 'InterMedium';
    font-size: 15px;
    line-height: 20px;
`;

const S_SubtitleRegular = styled.div`
    color: ${EColors2.DARK};
    font-family: 'InterMedium';
    font-size: 13px;
    line-height: 18px;
    margin-top: 10px;
`;

const S_InfoIconContainerRegular = styled.div`
    position: absolute;
    right: 10px;
    top: 10px;
    width: 40px;
    height: 40px;
    overflow: hidden;
`;
